// Two decorative blur circles that sit behind a hero section. Cheap,
// big visual impact -- used on the marketing homepage hero, the
// HouseProud sub-brand hero, and now per-trade landing-page heroes.
//
// Caller wraps content in `<section className="relative overflow-hidden">`
// and drops this inside as the first child; it absolute-positions to
// fill the parent at -z-10 so content renders on top.
import * as React from "react";

export type GradientHeroAccent =
  | "amber"
  | "blue"
  | "green"
  | "rose"
  | "violet"
  | "teal"
  | "orange";

export type GradientHeroIntensity = "subtle" | "medium";

export interface GradientHeroBackdropProps {
  accent?: GradientHeroAccent;
  intensity?: GradientHeroIntensity;
  className?: string;
}

// Each accent maps to two blur circles -- one at top-right, one at
// bottom-left -- in the same hue family but slightly different
// opacities so the gradient feels organic.
const ACCENT_TOP: Record<GradientHeroAccent, string> = {
  amber: "bg-amber-200/30",
  blue: "bg-blue-200/30",
  green: "bg-green-200/30",
  rose: "bg-rose-200/30",
  violet: "bg-violet-200/30",
  teal: "bg-teal-200/30",
  orange: "bg-orange-200/30",
};

const ACCENT_BOTTOM: Record<GradientHeroAccent, string> = {
  amber: "bg-amber-100/40",
  blue: "bg-blue-100/40",
  green: "bg-green-100/40",
  rose: "bg-rose-100/40",
  violet: "bg-violet-100/40",
  teal: "bg-teal-100/40",
  orange: "bg-orange-100/40",
};

const INTENSITY_OPACITY: Record<GradientHeroIntensity, string> = {
  subtle: "opacity-100",
  medium: "opacity-100 dark:opacity-70",
};

export function GradientHeroBackdrop({
  accent = "amber",
  intensity = "subtle",
  className,
}: GradientHeroBackdropProps) {
  return (
    <div
      aria-hidden="true"
      className={`absolute inset-0 -z-10 pointer-events-none ${INTENSITY_OPACITY[intensity]} ${className ?? ""}`}
    >
      <div
        className={`absolute -top-32 -right-32 w-96 h-96 rounded-full blur-3xl ${ACCENT_TOP[accent]}`}
      />
      <div
        className={`absolute -bottom-24 -left-24 w-80 h-80 rounded-full blur-3xl ${ACCENT_BOTTOM[accent]}`}
      />
    </div>
  );
}
